import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Select, Button, Dialog, Tooltip, Typography, useSelect, DataTable } from '@chilecompra/react-kit/components'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onContractDivisionsThunk } from '../ContractDivisions/ContractDivisions.actions'
import { onContractFilesThunk } from '../ContractFiles/ContractFiles.actions'
import { onContractDetailsThunk, onContractOrdersThunk, onNoContractDetailsThunk } from './ContractOrders.actions'
import ContractDivisions from '../ContractDivisions/ContractDivisions.container'
import ContractFiles from '../ContractFiles/ContractFiles.container'
import DualBarChart from '../../components/Charts/DualBarChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import ContractOrdersNoInfo from '../../components/Errors/ContractOrdersNoInfo'
import FichaContractOrders from '../../components/Infographics/FichaContractOrders'
import {
  Wrapper,
  GridWrapper,
  Exportable,
  Info,
  ContractLabel,
  Link,
  ModalContainer,
  Strong
} from './ContractOrders.styles'

/**
 * The ContractOrders' container.
 */
const ContractOrders = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity, name } = useSelector(state => state.buyerSection)
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const contractFilesRef = useRef()
  const inputSankeyChartRef = useRef()
  const dataTableRef = useRef()
  const [showTable, setShowTable] = useState(null)
  const [activeBar, setActiveBar] = useState('')
  const [expandArrow, setExpandArrow] = useState(false)
  const [open, setOpen] = useState(false)
  const [fileName, setFileName] = useState(`FichasDeContrato_Asociadas_${years[0].value}`)
  const {
    error,
    data: { NoContract, WithContract },
    details: { csvStructure, tableStructure },
    fetching: loading
  } = useSelector(state => state.contractOrders)
  const { value: yearsValue, onChange: handleChange } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onContractOrdersThunk(idEntity, updatedValue))
      dispatch(onContractDivisionsThunk(idEntity, updatedValue))
      dispatch(onContractFilesThunk(idEntity, updatedValue))
      setFileName(`FichasDeContrato_Asociadas_${updatedValue}`)
    }
  })
  const {
    details: { csvStructure: csvFilesStructure }
  } = useSelector(state => state.contractFiles)

  const handleContract = () => {
    contractFilesRef.current.close()
    setShowTable('details')
    setActiveBar('leftBar')
    setFileName(`FichasDeContrato_Asociadas_${yearsValue}`)
  }
  const handleNoContract = () => {
    contractFilesRef.current.close()
    setShowTable('details')
    setActiveBar('rightBar')
    setFileName(`FichasDeContrato_Asociadas_${yearsValue}`)
  }
  const handleUnloadTable = () => {
    setShowTable(null)
    setActiveBar('')
  }
  const handleShowTable = updatedValue => {
    if (updatedValue === 'files') {
      setFileName(`FichasDeContrato_Asociadas_Indicadores_${yearsValue}`)
    }

    setShowTable(updatedValue)
    setActiveBar('')
  }
  const handleExportData = () => exportToImage('contractChart', fileName)
  const handleCaptureImage = () => exportToImage('contractChart', fileName)
  const handleDownloadFile = () => exportToCsv(showTable === 'details' ? csvStructure : csvFilesStructure, fileName)
  const handleOpenModal = () => setOpen(true)
  const handleCloseModal = () => setOpen(false)

  useEffect(() => {
    if (!idEntity) {
      return
    }

    if (activeBar === 'leftBar' && showTable === 'details') {
      dispatch(onContractDetailsThunk(idEntity, yearsValue))
    }

    if (activeBar === 'rightBar' && showTable === 'details') {
      dispatch(onNoContractDetailsThunk(idEntity, yearsValue))
    }
  }, [activeBar, idEntity, showTable, yearsValue])

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onContractOrdersThunk(idEntity, yearsValue))
  }, [initialLoading, idEntity])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Fichas de contrato asociadas a sus órdenes de compra
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Para las compras mayores a 1000 UTM, la elaboración de un contrato entre el organismo comprador y el
            proveedor es requerida según lo indicado en el Art. 63 del Reglamento de Compras Públicas. Con el objetivo
            de transparentar la gestión de contratos de un organismo comprador en Mercado Público, se crea la ficha de
            contrato.
            <br />
            <br />
            <Link onClick={handleOpenModal}>¿Qué es una ficha de contrato?</Link>
            <Dialog onClose={handleCloseModal} open={open}>
              <ModalContainer>
                <Typography fontWeight="bold" variant="h3" margin="0 auto 0 0">
                  ¿Qué es una ficha de contrato?
                </Typography>
                <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 24px">
                  Considerando el tiempo de ejecución de un contrato, las fichas de contrato pueden ser clasificadas en
                  compras de ejecución inmediata o compras con ejecución en el tiempo; del mismo modo, se pueden
                  clasificar entre la adquisición de bienes o de servicios.
                  <br />
                  <br />
                  Por ejemplo, la compra de un teléfono celular correspondería a un contrato de ejecución inmediata de
                  un bien y la compra de un plan de telefonía celular con la entrega de un aparato telefónico sería un
                  contrato de ejecución en el tiempo de un servicio.
                  <br />
                  <br />
                  A su vez si se refiere a la adquisición de productos, el sistema permite identificar entre distintos
                  tipos de bienes (Alimentos, artículos de librería, combustibles, computadores, etc.). Por su parte las
                  adquisiciones de servicios se pueden desglosar en distintos tipos (Convenio de suministro,
                  inmobiliario, mantención de vehículos y equipamiento, etc.)
                  <br />
                  <br />
                </Typography>
                <Button onClick={handleCloseModal} margin="0 0 24px">
                  Cerrar
                </Button>
              </ModalContainer>
            </Dialog>
            <br />
            <br />
            Conoce cómo gestiona sus fichas de contrato <Strong>{name}</Strong> según el año seleccionado.
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper container item align="left" md={8} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearsValue} onChange={handleChange} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12} />
        </GridWrapper>
        <GridWrapper item align="left" md={2} xs={12} />
      </GridWrapper>
      <GridWrapper id="contractChart" container justifyContent="center" margin="8px auto 0">
        {error === null && (
          <GridWrapper item xs={12} md={10} className="barChart-container">
            <Typography variant="body2" fontSize="16px" lineHeight="18px" fontWeight="bold" margin="0 40% 0 0">
              {WithContract.value + NoContract.value} órdenes de compra requieren una ficha de contrato
              <Tooltip
                content={
                  <Typography color="white" fontSize="12px" lineHeight="16px" variant="body2">
                    Corresponden a las órdenes de compra con un monto superior a 1000 UTM que debiesen contar con una
                    ficha de contrato.
                  </Typography>
                }
                placement="top"
              >
                <Info />
              </Tooltip>
            </Typography>
            <DualBarChart
              activeBar={activeBar}
              NoContract={NoContract}
              WithContract={WithContract}
              handleContract={handleContract}
              handleNoContract={handleNoContract}
            />
            <Exportable ref={inputSankeyChartRef} onClick={handleExportData} />
          </GridWrapper>
        )}
        {showTable === 'details' && (
          <>
            <GridWrapper item xs={12} md={10} margin="16px 0 0">
              <GridWrapper container justifyContent="space-between">
                <GridWrapper item xs={12} md={6}>
                  <Typography fontSize="14px" fontWeight="bold">
                    {activeBar === 'leftBar'
                      ? 'Órdenes de compra con ficha de contrato'
                      : 'Órdenes de compra sin ficha de contrato'}
                  </Typography>
                </GridWrapper>
                <GridWrapper item xs={12} md={6} align="right">
                  <IconButton type="close" fontWeight="bold" fontSize="14px" onClick={handleUnloadTable}>
                    Cerrar
                  </IconButton>
                </GridWrapper>
              </GridWrapper>
            </GridWrapper>
            <GridWrapper item margin="24px 0 0" md={10} minHeight="250px" xs={12}>
              <DataTable
                headers={[
                  { label: 'Código ficha de contrato', value: 'contractCode' },
                  { label: 'Número de orden de compra', value: 'orderCode' },
                  { label: 'Monto orden de compra (UTM)', value: 'amountUTM' },
                  { label: 'Estado de la orden de compra', value: 'status' }
                ]}
                headersCellsProps={[
                  { enableFilter: true, enableSort: true },
                  { enableFilter: true, enableSort: true },
                  { enableFilter: true, enableSort: true },
                  { enableFilter: true, enableSort: true }
                ]}
                headersDecorators={[
                  () => (
                    <>
                      Código ficha
                      <br /> de contrato
                    </>
                  ),
                  () => (
                    <>
                      Número de
                      <br /> orden de compra
                    </>
                  ),
                  () => (
                    <>
                      Monto orden
                      <br /> de compra (UTM)
                    </>
                  ),
                  () => (
                    <>
                      Estado de la
                      <br /> orden de compra
                    </>
                  )
                ]}
                initialRows={6}
                loading={loading}
                records={tableStructure}
                recordsDecorators={[
                  ({ contractCode, linkContrato }) => (
                    <Link href={linkContrato} target="_blank" rel="noopener noreferrer">
                      {contractCode === '0' ? <ContractLabel label="No Existe" /> : contractCode}
                    </Link>
                  ),
                  ({ link, orderCode }) => (
                    <Link href={link} target="_blank" rel="noopener noreferrer">
                      {orderCode}
                    </Link>
                  ),
                  ({ amountUTM }) => amountUTM,
                  ({ status }) => status
                ]}
                ref={dataTableRef}
              />
            </GridWrapper>
          </>
        )}
        {error !== null && (
          <GridWrapper item xs={12} md={10}>
            <ContractOrdersNoInfo />
          </GridWrapper>
        )}
        {error === null && (
          <>
            <GridWrapper item xs={12} md={10} align="center">
              <ContractFiles ref={contractFilesRef} year={yearsValue} onOpen={handleShowTable} />
            </GridWrapper>
            <GridWrapper item xs={12} md={10} align="center">
              <ContractDivisions year={yearsValue} />
            </GridWrapper>
          </>
        )}
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10} margin="32px auto 0">
          <Infobar
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<FichaContractOrders />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default ContractOrders
